import React, { useState, useEffect, useRef } from "react";
import {
  Loader2,
  AlertCircle,
  Search,
  ChevronDown,
  ExternalLink,
  DollarSign,
  Calendar,
  TrendingUp,
  Info,
} from "lucide-react";
import {
  approvalService,
  Approval,
  ApproveApprovalRequest,
  ApprovalSubscriptionSearchResult,
} from "../../services/ApprovalService";
import { useToastContext } from "../../context/ToastContext";

type ApprovalStatus = "pending" | "approved" | "denied" | "all";
type ProcessingAction = "approve" | "deny" | "link" | "unlink";

interface ApprovalsProps {
  embedded?: boolean;
  onCountsChange?: (data: { count: number; status: ApprovalStatus }) => void;
}

const Approvals: React.FC<ApprovalsProps> = ({
  embedded = false,
  onCountsChange,
}) => {
  const { showToast } = useToastContext();
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<ApprovalStatus>("pending");
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const [processingAction, setProcessingAction] = useState<{
    approvalId: string;
    action: ProcessingAction;
  } | null>(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    Record<string, string>
  >({});
  const [denyReason, setDenyReason] = useState<Record<string, string>>({});
  const [showDenyReason, setShowDenyReason] = useState<Record<string, boolean>>(
    {},
  );
  const [searchOpen, setSearchOpen] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState<Record<string, string>>({});
  const [searchStatus, setSearchStatus] = useState<Record<string, string>>({});
  const [searchResults, setSearchResults] = useState<
    Record<string, ApprovalSubscriptionSearchResult[]>
  >({});
  const [searchLoading, setSearchLoading] = useState<Record<string, boolean>>(
    {},
  );
  const [searchError, setSearchError] = useState<Record<string, string>>({});
  const [expandedApprovals, setExpandedApprovals] = useState<
    Record<string, boolean>
  >({});
  const [autoCreateSubscriptions, setAutoCreateSubscriptions] = useState(false);

  useEffect(() => {
    loadApprovals();
  }, [statusFilter]);

  useEffect(() => {
    onCountsChange?.({ count: approvals.length, status: statusFilter });
  }, [approvals.length, statusFilter, onCountsChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        statusFilterRef.current &&
        !statusFilterRef.current.contains(event.target as Node)
      ) {
        setIsStatusFilterOpen(false);
      }
    };

    if (isStatusFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isStatusFilterOpen]);

  const loadApprovals = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await approvalService.getApprovals(
        statusFilter === "all" ? undefined : statusFilter,
      );
      setApprovals(response.data.approvals);
      setAutoCreateSubscriptions(
        response.data.settings?.autoCreateSubscriptions ?? false,
      );
    } catch (error: any) {
      console.error("Error loading approvals:", error);
      if (!silent) showToast("error", error.message || "Failed to load approvals");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleApprove = async (
    approval: Approval,
    action: ProcessingAction = "approve",
  ) => {
    setProcessingAction({ approvalId: approval._id, action });
    try {
      const request: ApproveApprovalRequest = {};
      if (selectedSubscriptionId[approval._id]) {
        request.subscriptionId = selectedSubscriptionId[approval._id];
      }

      const response = await approvalService.approveApproval(
        approval._id,
        request,
      );
      const success =
        response.status === 200 && response.data?.data?.success === true;
      if (success) {
        showToast(
          "success",
          response.data.data?.message ||
            response.data.message ||
            "Subscription linked successfully",
        );
        await loadApprovals(true);
        // Clear selected subscription
        setSelectedSubscriptionId((prev) => {
          const next = { ...prev };
          delete next[approval._id];
          return next;
        });
      } else {
        showToast(
          "error",
          response.data?.data?.message ||
            response.data?.message ||
            "Failed to link subscription",
        );
      }
    } catch (error: any) {
      console.error("Error approving:", error);
      showToast("error", error.message || "Failed to approve");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleDeny = async (approval: Approval) => {
    setProcessingAction({ approvalId: approval._id, action: "deny" });
    try {
      const response = await approvalService.denyApproval(approval._id, {
        reason: denyReason[approval._id],
      });
      showToast("success", "Approval denied successfully");
      await loadApprovals();
      // Clear deny reason
      setDenyReason((prev) => {
        const next = { ...prev };
        delete next[approval._id];
        return next;
      });
      setShowDenyReason((prev) => {
        const next = { ...prev };
        delete next[approval._id];
        return next;
      });
    } catch (error: any) {
      console.error("Error denying:", error);
      showToast("error", error.message || "Failed to deny approval");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleSearchSubscriptions = async (approvalId: string) => {
    const query = (searchQuery[approvalId] || "").trim();
    if (!query) {
      setSearchError((prev) => ({
        ...prev,
        [approvalId]: "Enter a subscription ID, customer ID, or email.",
      }));
      return;
    }

    setSearchLoading((prev) => ({ ...prev, [approvalId]: true }));
    setSearchError((prev) => {
      const next = { ...prev };
      delete next[approvalId];
      return next;
    });

    try {
      const status =
        searchStatus[approvalId] && searchStatus[approvalId] !== "all"
          ? searchStatus[approvalId]
          : undefined;

      const response = await approvalService.searchStripeSubscriptions({
        query,
        status,
      });
      setSearchResults((prev) => ({
        ...prev,
        [approvalId]: response.data.results || [],
      }));
    } catch (error: any) {
      console.error("Error searching subscriptions:", error);
      setSearchError((prev) => ({
        ...prev,
        [approvalId]: error.message || "Failed to search subscriptions.",
      }));
      setSearchResults((prev) => ({
        ...prev,
        [approvalId]: [],
      }));
    } finally {
      setSearchLoading((prev) => ({ ...prev, [approvalId]: false }));
    }
  };

  const handleLinkSubscription = async (
    approval: Approval,
    subscriptionId: string,
  ) => {
    setProcessingAction({ approvalId: approval._id, action: "link" });
    try {
      const request: ApproveApprovalRequest = { subscriptionId };
      const response = await approvalService.approveApproval(
        approval._id,
        request,
      );
      const success =
        response.status === 200 && response.data?.data?.success === true;
      if (success) {
        showToast(
          "success",
          response.data.data?.message ||
            response.data.message ||
            "Subscription linked successfully",
        );
        await loadApprovals(true);
        setSearchResults((prev) => ({
          ...prev,
          [approval._id]: [],
        }));
        setSearchQuery((prev) => ({
          ...prev,
          [approval._id]: "",
        }));
      } else {
        showToast(
          "error",
          response.data?.data?.message ||
            response.data?.message ||
            "Failed to link subscription",
        );
      }
    } catch (error: any) {
      console.error("Error linking subscription:", error);
      showToast("error", error.message || "Failed to link subscription");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleUnlink = async (approval: Approval) => {
    setProcessingAction({ approvalId: approval._id, action: "unlink" });
    try {
      const response = await approvalService.unlinkApproval(approval._id);
      const success =
        response.status === 200 && response.data?.data?.success === true;
      if (success) {
        showToast(
          "success",
          response.data.data?.message ||
            response.data.message ||
            "Subscription unlinked successfully",
        );
        await loadApprovals(true);
      } else {
        showToast(
          "error",
          response.data?.data?.message ||
            response.data?.message ||
            "Failed to unlink subscription",
        );
      }
    } catch (error: any) {
      console.error("Error unlinking:", error);
      showToast("error", error.message || "Failed to unlink subscription");
    } finally {
      setProcessingAction(null);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatStripeDate = (unixSeconds?: number | null) => {
    if (!unixSeconds) return "—";
    const date = new Date(unixSeconds * 1000);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatStripeAmount = (amount: number, currency?: string | null) => {
    if (!currency) return formatCurrency(amount);
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return formatCurrency(amount);
    }
  };

  const getConfidenceColor = (confidence?: "high" | "medium" | "low") => {
    switch (confidence) {
      case "high":
        return "bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800/30";
      case "medium":
        return "bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800/30";
      case "low":
        return "bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800/30";
      default:
        return "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700/50";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400";
      case "denied":
        return "bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400";
      case "pending":
        return "bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400";
      default:
        return "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300";
    }
  };

  const getSourceLabel = (source?: "hubspot" | "stripe") => {
    if (source === "stripe") return "From Stripe subscription";
    if (source === "hubspot") return "From HubSpot deal";
    return null;
  };

  const getOrphanTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      deal_without_subscription: "Deal Without Subscription",
      subscription_without_deal: "Subscription Without Deal",
      deal_without_company: "Deal Without Company",
      globalcustomer_without_match: "Global Customer Without Match",
      deal_without_contract: "Deal Without Contract",
    };
    return labels[type] || type;
  };

  const searchStatusOptions = [
    { value: "all", label: "All statuses" },
    { value: "active", label: "Active" },
    { value: "trialing", label: "Trialing" },
    { value: "past_due", label: "Past due" },
    { value: "unpaid", label: "Unpaid" },
    { value: "canceled", label: "Canceled" },
    { value: "incomplete", label: "Incomplete" },
    { value: "incomplete_expired", label: "Incomplete expired" },
  ];

  const containerClasses = embedded
    ? "w-full"
    : "w-full min-h-screen bg-background-default dark:bg-background-dark";
  const innerClasses = embedded ? "w-full" : "max-w-7xl mx-auto px-6 py-6";
  const titleClasses = embedded
    ? "text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-1"
    : "text-3xl font-semibold text-text-primary dark:text-text-primaryDark mb-2";
  const subtitleClasses = embedded
    ? "text-sm text-text-secondary dark:text-text-secondaryDark"
    : "text-base text-text-secondary dark:text-text-secondaryDark";

  return (
    <div className={containerClasses}>
      <div className={innerClasses}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className={titleClasses}>Approvals</h1>
            <p className={subtitleClasses}>
              Review and approve contracts that need Stripe subscription linking
            </p>
          </div>
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-4 mb-4">
          <div className="relative" ref={statusFilterRef}>
            <button
              onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)}
              className="px-3.5 py-2 text-sm font-medium bg-background-paper dark:bg-background-paperDark border border-divider/60 dark:border-divider-dark/60 rounded-lg text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-divider dark:hover:border-divider-dark transition-all duration-150 flex items-center gap-2 min-w-[130px] justify-between shadow-sm hover:shadow"
            >
              <span>
                {statusFilter === "all"
                  ? "All Status"
                  : statusFilter.charAt(0).toUpperCase() +
                    statusFilter.slice(1)}
              </span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  isStatusFilterOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {isStatusFilterOpen && (
              <div className="absolute top-full left-0 mt-1.5 bg-background-paper dark:bg-background-paperDark border border-divider/60 dark:border-divider-dark/60 rounded-lg shadow-xl z-50 min-w-[130px] overflow-hidden backdrop-blur-sm">
                {[
                  { value: "all", label: "All Status" },
                  { value: "pending", label: "Pending" },
                  { value: "approved", label: "Approved" },
                  { value: "denied", label: "Denied" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setStatusFilter(option.value as ApprovalStatus);
                      setIsStatusFilterOpen(false);
                    }}
                    className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors ${
                      statusFilter === option.value
                        ? "bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary font-medium"
                        : "text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="text-sm text-text-secondary dark:text-text-secondaryDark">
            {approvals.length} approval{approvals.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Approvals List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-secondary" />
          </div>
        ) : approvals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-text-secondary dark:text-text-secondaryDark mb-2">
              No approvals found
            </p>
            <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
              {statusFilter === "pending"
                ? "All contracts are properly linked"
                : "No approvals match the selected filter"}
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {approvals.map((approval) => {
              const indicators = approval.actionIndicators;
              const hasMatch = indicators?.hasMatch ?? false;
              const canSearch = indicators?.canSearch ?? true;
              const canCreateNew =
                indicators?.canCreateNew ?? autoCreateSubscriptions;
              const actionDescription = indicators?.actionDescription;
              const showConfidenceTag = hasMatch && approval.matchConfidence;
              const isRowProcessing =
                processingAction?.approvalId === approval._id;
              const isApproving =
                isRowProcessing && processingAction?.action === "approve";
              const isDenying =
                isRowProcessing && processingAction?.action === "deny";
              const isLinking =
                isRowProcessing && processingAction?.action === "link";
              const isUnlinking =
                isRowProcessing && processingAction?.action === "unlink";
              const isRenewal = approval.renewal?.isRenewal === true;
              const renewalStartDate =
                approval.renewal?.renewalEffectiveDate ||
                approval.contractId.endDate ||
                null;
              const renewalStartLabel = renewalStartDate
                ? formatDate(renewalStartDate)
                : "Unknown";

              return (
                <div
                  key={approval._id}
                  className="rounded-2xl border border-divider/60 dark:border-divider-dark/60 bg-background-paper dark:bg-background-paperDark px-6 py-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <h3 className="text-base font-semibold text-text-primary dark:text-text-primaryDark truncate">
                          {approval.contractId.contractName ||
                            "Unnamed Contract"}
                        </h3>
                        {isRenewal && (
                          <span className="px-2.5 py-0.5 rounded-md text-xs font-medium border border-secondary/60 text-secondary dark:text-secondary">
                            Renewal
                          </span>
                        )}
                        {getSourceLabel(approval.source) && (
                          <span className="px-2.5 py-0.5 rounded-md text-xs font-medium border border-divider/60 dark:border-divider-dark/60 text-text-secondary dark:text-text-secondaryDark">
                            {getSourceLabel(approval.source)}
                          </span>
                        )}
                        <span className="px-2.5 py-0.5 rounded-md text-xs font-medium border border-divider/60 dark:border-divider-dark/60 text-text-secondary dark:text-text-secondaryDark capitalize">
                          {approval.status}
                        </span>
                        <span
                          className={`px-2.5 py-0.5 rounded-md text-xs font-medium border ${
                            hasMatch
                              ? "border-purple-300/60 dark:border-purple-700/40 text-purple-700 dark:text-purple-300"
                              : "border-divider/60 dark:border-divider-dark/60 text-text-secondary dark:text-text-secondaryDark"
                          }`}
                        >
                          {hasMatch ? "Match Found" : "No Match Found"}
                        </span>
                        {showConfidenceTag && (
                          <span
                            className={`px-2.5 py-0.5 rounded-md text-xs font-medium border ${getConfidenceColor(
                              approval.matchConfidence,
                            )}`}
                          >
                            {approval.matchConfidence} confidence
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-xs text-text-secondary dark:text-text-secondaryDark">
                        {approval.contractId.hubspotDealId && (
                          <span className="font-mono text-[11px]">
                            Deal ID: {approval.contractId.hubspotDealId}
                          </span>
                        )}
                        {isRenewal && (
                          <span>
                            Renewal start{" "}
                            <span className="font-medium text-text-primary dark:text-text-primaryDark">
                              {renewalStartLabel}
                            </span>
                          </span>
                        )}
                        <span>
                          MRR{" "}
                          <span className="font-medium text-text-primary dark:text-text-primaryDark">
                            {formatCurrency(approval.contractId.mrr)}
                          </span>
                        </span>
                        <span>
                          ARR{" "}
                          <span className="font-medium text-text-primary dark:text-text-primaryDark">
                            {formatCurrency(approval.contractId.arr)}
                          </span>
                        </span>
                        <span>
                          {formatDate(approval.contractId.startDate)} -{" "}
                          {formatDate(approval.contractId.endDate)}
                        </span>
                      </div>
                      {actionDescription && (
                        <div className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1">
                          {actionDescription}
                        </div>
                      )}
                      {isRenewal && (
                        <div className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1">
                          This renewal subscription will start on the current
                          term end date.
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedApprovals((prev) => ({
                          ...prev,
                          [approval._id]: !prev[approval._id],
                        }))
                      }
                      className="px-3 py-1 text-xs font-medium text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark border border-divider/60 dark:border-divider-dark/60 rounded-md hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors"
                    >
                      {expandedApprovals[approval._id]
                        ? "Hide details"
                        : "Show details"}
                    </button>
                  </div>
                  {approval.status === "pending" && (
                    <div className="mt-4 pt-4 border-t border-divider/60 dark:border-divider-dark/60 flex flex-wrap items-center gap-3">
                      {hasMatch && (
                        <button
                          onClick={() => handleApprove(approval, "approve")}
                          disabled={isRowProcessing}
                          className="inline-flex items-center gap-2 px-3 py-1.5 border border-blue-500/50 text-blue-600 dark:text-blue-400 rounded-md text-sm font-medium transition-colors hover:bg-blue-50/40 dark:hover:bg-blue-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isApproving && (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          )}
                          Approve to Link
                        </button>
                      )}
                      {!hasMatch && canCreateNew && (
                        <button
                          onClick={() => handleApprove(approval, "approve")}
                          disabled={isRowProcessing}
                          className="inline-flex items-center gap-2 px-3 py-1.5 border border-secondary/60 text-secondary rounded-md text-sm font-medium transition-colors hover:bg-secondary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isApproving && (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          )}
                          Create New Subscription
                        </button>
                      )}
                      {canSearch && (
                        <button
                          onClick={() => {
                            setExpandedApprovals((prev) => ({
                              ...prev,
                              [approval._id]: true,
                            }));
                            setSearchOpen((prev) => ({
                              ...prev,
                              [approval._id]: true,
                            }));
                          }}
                          className="inline-flex items-center gap-2 px-3 py-1.5 border border-divider/60 dark:border-divider-dark/60 text-text-secondary dark:text-text-secondaryDark rounded-md text-sm font-medium transition-colors hover:bg-background-panel dark:hover:bg-background-panelDark hover:text-text-primary dark:hover:text-text-primaryDark"
                        >
                          {hasMatch
                            ? "Search Different"
                            : "Search Subscription"}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (showDenyReason[approval._id]) {
                            handleDeny(approval);
                          } else {
                            setExpandedApprovals((prev) => ({
                              ...prev,
                              [approval._id]: true,
                            }));
                            setShowDenyReason((prev) => ({
                              ...prev,
                              [approval._id]: true,
                            }));
                          }
                        }}
                        disabled={isRowProcessing}
                        className="inline-flex items-center gap-2 px-3 py-1.5 border border-red-500/50 text-red-600 dark:text-red-400 rounded-md text-sm font-medium transition-colors hover:bg-red-50/40 dark:hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isDenying && (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        )}
                        {showDenyReason[approval._id] ? "Confirm Deny" : "Deny"}
                      </button>
                      {!hasMatch && !canCreateNew && (
                        <span className="text-[11px] text-text-secondary dark:text-text-secondaryDark">
                          Enable auto-create subscriptions in settings to create
                          new subscriptions
                        </span>
                      )}
                    </div>
                  )}

                  {expandedApprovals[approval._id] && (
                    <div className="mt-2 pt-3 border-t border-divider/50 dark:border-divider-dark/50">
                      {/* Contract Details */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-2.5">
                          <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1">
                            MRR
                          </div>
                          <div className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                            {formatCurrency(approval.contractId.mrr)}
                          </div>
                        </div>
                        <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-2.5">
                          <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1">
                            ARR
                          </div>
                          <div className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                            {formatCurrency(approval.contractId.arr)}
                          </div>
                        </div>
                        <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-2.5">
                          <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1">
                            Start Date
                          </div>
                          <div className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                            {formatDate(approval.contractId.startDate)}
                          </div>
                        </div>
                        <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-2.5">
                          <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1">
                            End Date
                          </div>
                          <div className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                            {formatDate(approval.contractId.endDate)}
                          </div>
                        </div>
                      </div>

                      {/* Match Details */}
                      {approval.matchDetails && (
                        <div className="bg-background-default dark:bg-background-dark border border-divider/60 dark:border-divider-dark/60 rounded-lg p-2.5 mb-3">
                          <div className="flex items-start gap-2 mb-2">
                            <Info className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark mt-0.5" />
                            <h4 className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
                              Match Details
                            </h4>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
                            <div>
                              <span className="text-text-secondary dark:text-text-secondaryDark">
                                Domain Match:{" "}
                              </span>
                              <span className="font-medium">
                                {approval.matchDetails.domainMatch
                                  ? "Yes"
                                  : "No"}
                              </span>
                            </div>
                            <div>
                              <span className="text-text-secondary dark:text-text-secondaryDark">
                                Line Items Amount:{" "}
                              </span>
                              <span className="font-medium">
                                {approval.matchDetails.lineItemsAmount !==
                                  undefined &&
                                approval.matchDetails.lineItemsAmount !== null
                                  ? formatCurrency(
                                      approval.matchDetails.lineItemsAmount,
                                    )
                                  : "—"}
                              </span>
                            </div>
                            <div>
                              <span className="text-text-secondary dark:text-text-secondaryDark">
                                Subscription Amount:{" "}
                              </span>
                              <span className="font-medium">
                                {approval.matchDetails.subscriptionAmount !==
                                  undefined &&
                                approval.matchDetails.subscriptionAmount !==
                                  null
                                  ? formatCurrency(
                                      approval.matchDetails.subscriptionAmount,
                                    )
                                  : "—"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Suggested Subscription */}
                      {approval.suggestedSubscription && (
                        <div className="bg-background-default dark:bg-background-dark rounded-lg p-2.5 mb-3 border border-divider/60 dark:border-divider-dark/60">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div>
                              <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1">
                                Suggested Subscription
                              </div>
                              <a
                                href={`https://dashboard.stripe.com/subscriptions/${approval.suggestedSubscription.subscriptionId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs font-mono text-[#8B5CF6] hover:text-[#7C3AED] transition-colors"
                              >
                                {approval.suggestedSubscription.subscriptionId}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400">
                              Suggested
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] text-text-secondary dark:text-text-secondaryDark">
                            <div>
                              <span className="font-medium text-text-secondary dark:text-text-secondaryDark">
                                Product
                              </span>
                              <div>
                                {approval.suggestedSubscription.productName ||
                                  "—"}
                              </div>
                            </div>
                            <div>
                              <span className="font-medium text-text-secondary dark:text-text-secondaryDark">
                                Customer
                              </span>
                              <div>
                                {approval.suggestedSubscription.customerName ||
                                  approval.suggestedSubscription
                                    .customerEmail ||
                                  "—"}
                              </div>
                            </div>
                            <div>
                              <span className="font-medium text-text-secondary dark:text-text-secondaryDark">
                                Amount
                              </span>
                              <div>
                                {approval.suggestedSubscription.amount !==
                                  undefined &&
                                approval.suggestedSubscription.amount !== null
                                  ? formatStripeAmount(
                                      approval.suggestedSubscription.amount,
                                      approval.suggestedSubscription.currency ||
                                        null,
                                    )
                                  : "—"}
                              </div>
                            </div>
                            <div>
                              <span className="font-medium text-text-secondary dark:text-text-secondaryDark">
                                Status
                              </span>
                              <div>
                                {approval.suggestedSubscription.status || "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Alternative Matches */}
                      {approval.alternativeMatches &&
                        approval.alternativeMatches.length > 0 && (
                          <div className="mb-3">
                            <label className="block text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5">
                              Alternative Matches
                            </label>
                            <select
                              value={selectedSubscriptionId[approval._id] || ""}
                              onChange={(e) =>
                                setSelectedSubscriptionId((prev) => ({
                                  ...prev,
                                  [approval._id]: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-2 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary"
                            >
                              <option value="">
                                {approval.stripeSubscriptionId
                                  ? "Use suggested subscription"
                                  : "Select a subscription..."}
                              </option>
                              {approval.alternativeMatches.map((match) => (
                                <option
                                  key={match.subscriptionId}
                                  value={match.subscriptionId}
                                >
                                  {match.subscriptionId} ({match.confidence}{" "}
                                  confidence, {Math.round(match.score * 100)}%
                                  match)
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                      {/* Manual Search */}
                      <div className="mb-3">
                        <button
                          type="button"
                          onClick={() =>
                            setSearchOpen((prev) => ({
                              ...prev,
                              [approval._id]: !prev[approval._id],
                            }))
                          }
                          className="inline-flex items-center gap-2 text-xs font-medium text-secondary hover:text-secondary-hover"
                        >
                          <Search className="w-4 h-4" />
                          Search Stripe Subscriptions (Exact match)
                          <ChevronDown
                            className={`w-4 h-4 transition-transform ${
                              searchOpen[approval._id] ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        <div className="mt-1 text-[11px] text-text-secondary dark:text-text-secondaryDark">
                          Exact match only for subscription ID, customer ID or
                          customer email.
                        </div>

                        {searchOpen[approval._id] && (
                          <div className="mt-3 bg-background-default dark:bg-background-dark rounded-lg border border-divider/60 dark:border-divider-dark/60 p-4">
                            <div className="grid grid-cols-1 md:grid-cols-[1fr,200px,auto] gap-4 items-end">
                              <div>
                                <label className="block text-[11px] font-medium text-text-secondary dark:text-text-secondaryDark mb-1">
                                  Subscription ID, customer ID or email
                                </label>
                                <input
                                  type="text"
                                  value={searchQuery[approval._id] || ""}
                                  onChange={(e) =>
                                    setSearchQuery((prev) => ({
                                      ...prev,
                                      [approval._id]: e.target.value,
                                    }))
                                  }
                                  placeholder="Enter subscription ID, customer ID, or email"
                                  className="w-full px-3 py-2 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary"
                                />
                              </div>
                              <div>
                                <label className="block text-[11px] font-medium text-text-secondary dark:text-text-secondaryDark mb-1">
                                  Status
                                </label>
                                <div className="relative">
                                  <select
                                    value={searchStatus[approval._id] || "all"}
                                    onChange={(e) =>
                                      setSearchStatus((prev) => ({
                                        ...prev,
                                        [approval._id]: e.target.value,
                                      }))
                                    }
                                    className="w-full appearance-none px-3 pr-10 py-2 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary"
                                  >
                                    {searchStatusOptions.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                                </div>
                              </div>
                              <div className="flex items-end">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleSearchSubscriptions(approval._id)
                                  }
                                  disabled={searchLoading[approval._id]}
                                  className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-secondary hover:bg-secondary-hover text-white rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed"
                                >
                                  {searchLoading[approval._id] ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Search className="w-4 h-4" />
                                  )}
                                  Search
                                </button>
                              </div>
                            </div>

                            {searchError[approval._id] && (
                              <div className="mt-3 text-xs text-red-600 dark:text-red-400">
                                {searchError[approval._id]}
                              </div>
                            )}

                            {!searchLoading[approval._id] &&
                              searchResults[approval._id] &&
                              searchResults[approval._id].length === 0 &&
                              (searchQuery[approval._id] || "") && (
                                <div className="mt-3 text-xs text-text-secondary dark:text-text-secondaryDark">
                                  No subscriptions found. Try a different query.
                                </div>
                              )}

                            {searchResults[approval._id] &&
                              searchResults[approval._id].length > 0 && (
                                <div className="mt-4 space-y-3">
                                  {searchResults[approval._id].map((result) => (
                                    <div
                                      key={result.id}
                                      className="bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider/60 dark:border-divider-dark/60 p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                                    >
                                      <div className="space-y-1">
                                        <div className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                                          {result.id}
                                        </div>
                                        <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
                                          Status: {result.status} • Items:{" "}
                                          {result.itemsCount}
                                        </div>
                                        <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
                                          Customer:{" "}
                                          {result.customerEmail ||
                                            result.customerId ||
                                            "—"}
                                        </div>
                                        <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
                                          Created:{" "}
                                          {formatStripeDate(result.created)} •
                                          Period End:{" "}
                                          {formatStripeDate(
                                            result.currentPeriodEnd,
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                                          {formatStripeAmount(
                                            result.amount,
                                            result.currency,
                                          )}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleLinkSubscription(
                                              approval,
                                              result.id,
                                            )
                                          }
                                          disabled={isRowProcessing}
                                          className="inline-flex items-center gap-2 px-3 py-1.5 border border-green-500/50 text-green-600 dark:text-green-400 rounded-md text-sm font-medium transition-colors hover:bg-green-50/40 dark:hover:bg-green-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {isLinking && (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                          )}
                                          Link This Subscription
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                          </div>
                        )}
                      </div>

                      {/* Action: Create New Subscription */}
                      {!approval.stripeSubscriptionId &&
                        !approval.alternativeMatches?.length && (
                          <div className="bg-background-default dark:bg-background-dark border border-divider/60 dark:border-divider-dark/60 rounded-lg p-2.5 mb-3">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-xs text-text-secondary dark:text-text-secondaryDark">
                                  No matching subscription found. Approving will
                                  create a new Stripe subscription.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* Deny Reason Input */}
                      {showDenyReason[approval._id] && (
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5">
                            Reason for Denial (Optional)
                          </label>
                          <textarea
                            value={denyReason[approval._id] || ""}
                            onChange={(e) =>
                              setDenyReason((prev) => ({
                                ...prev,
                                [approval._id]: e.target.value,
                              }))
                            }
                            placeholder="Enter reason for denial..."
                            className="w-full px-3 py-2 bg-background-default dark:bg-background-dark border border-divider dark:border-divider-dark rounded-lg text-sm text-text-primary dark:text-text-primaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary resize-none"
                            rows={2}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions moved to right column */}
                  {/* Approved/Denied Info */}
                  {approval.status === "approved" && approval.approvedAt && (
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
                        Approved on {formatDate(approval.approvedAt)}
                        {approval.approvedSubscriptionId && (
                          <span className="ml-2">
                            • Subscription: {approval.approvedSubscriptionId}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleUnlink(approval)}
                        disabled={isRowProcessing}
                        className="inline-flex items-center gap-2 px-3 py-1.5 border border-red-500/40 text-red-600 dark:text-red-400 rounded-md text-sm font-medium transition-colors hover:bg-red-50/40 dark:hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUnlinking && (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        )}
                        Unlink
                      </button>
                    </div>
                  )}
                  {approval.status === "denied" && approval.deniedAt && (
                    <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
                      Denied on {formatDate(approval.deniedAt)}
                      {approval.deniedReason && (
                        <span className="ml-2">
                          • Reason: {approval.deniedReason}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Approvals;
